/**
 * Battle-resolution tuning constants (#18). Sourced from
 * `docs/game-details/battle_system.md` and `docs/game-details/ships.md`
 * "Ship Abilities", with the rule changes finalized in the battle-system
 * implementation plan (round counts, hit decay, priority-bonus multiplier,
 * shield-ship taunt, and the id3/id5 skill swap — see `constants/ships.ts`)
 * taking precedence wherever they conflict with the docs.
 */
// ---------------------------------------------------------------------------
// Hits, decay, targeting
// ---------------------------------------------------------------------------
/** Each unit group attacks up to 3 times per round, at 100% / 66% / 33% of computed damage. */
export const BATTLE_HIT_STEP_MULTIPLIERS = [1, 0.66, 0.33];
export const BATTLE_MAX_HITS_PER_GROUP = BATTLE_HIT_STEP_MULTIPLIERS.length;
/** `bonusTargets` damage multiplier (was 1.7x). */
export const BATTLE_PRIORITY_BONUS_MULTIPLIER = 1.5;
/** `penaltyTargets` damage multiplier — unchanged. */
export const BATTLE_PENALTY_MULTIPLIER = 0.7;
/** Aegis/Fortress/Chiton — always the first target for the opposing side while any are alive. */
export const SHIELD_TAUNT_SHIP_ID = 5;
// ---------------------------------------------------------------------------
// Critical damage (Devastate skill + Surprise Tactics science, which already
// exists via `getScienceBonus().surpriseTactics.criticalChance`)
// ---------------------------------------------------------------------------
export const CRITICAL_DAMAGE_MIN_MULTIPLIER = 2;
export const CRITICAL_DAMAGE_MAX_MULTIPLIER = 2.5;
/**
 * Surprise Tactics crit chance, per level, linear to its `maxLevel: 10` cap
 * (→ 1/3 = 33.3% total at level 10). Deliberately diverges from the original
 * Nemexia design (1%/level for levels 1-8, 2%/level for 9-10, capped at 12%
 * total) — see `docs/game-details/sciences.md` "Surprise Tactics".
 */
export const SURPRISE_TACTICS_CRIT_CHANCE_PER_LEVEL = 1 / 30;
// ---------------------------------------------------------------------------
// Ship skills — chance/magnitude per ship present, capped. Percentages are
// fractions (0.00035 = 0.035%), matching `docs/game-details/ships.md`
// "Ship Abilities" (id3/id5 swapped per the plan — see constants/ships.ts).
// ---------------------------------------------------------------------------
/** id5 (post-swap): when active, every OTHER friendly group ignores enemy armor for one round (never the providing group itself). Activates once/round.
 * Values are adjusted for id5 to match the previous settings for id3 per capita
*/
export const IGNORE_ARMOR_CHANCE_PER_SHIP = {
    humans: 0.000210,
    robots: 0.000245,
    axons: 0.000105,
};
export const IGNORE_ARMOR_MAX_CHANCE = 0.70;
/** id4: chance to deal critical damage (`CRITICAL_DAMAGE_MIN..MAX_MULTIPLIER`), race-specific rate. */
export const DEVASTATE_CHANCE_PER_SHIP = {
    humans: 0.00050,
    robots: 0.00036,
    axons: 0.00015,
};
export const DEVASTATE_MAX_CHANCE = 0.50;
/**
 * id3 (post-swap): passive +life to every OTHER friendly group, per ship
 * present — race-specific rate (`ships.md` "Bonus Life": Aegis 0.050% /
 * Fortress 0.075% / Chiton 0.030%, cap 30% at 600/400/1000 ships).
 * Values are adjusted for id3 to match the previous settings for id5 per capita
 */
export const BONUS_LIFE_PER_SHIP = {
    humans: 0.00020,
    robots: 0.00020,
    axons: 0.00010,
};
export const BONUS_LIFE_MAX = 0.30;
/**
 * id6: passive +armor to every OTHER friendly group, per ship present —
 * race-specific rate (`ships.md` "Armor Boost": Bull 0.038% /
 * Lattice 0.025% / Spearhead 0.018%, cap 30% at 789/1200/1666 ships).
 */
export const ARMOR_BOOST_PER_SHIP = {
    humans: 0.00038,
    robots: 0.00025,
    axons: 0.00018,
};
export const ARMOR_BOOST_MAX = 0.30;
/**
 * id8: +50% damage to enemy DEFENSES only. Computed for completeness (chance
 * formula) but has no effect yet — defenses don't exist (deferred).
 */
export const ARTILLERY_CHANCE_PER_SHIP = {
    humans: 0.0010,
    robots: 0.0009,
    axons: 0.0009,
};
export const ARTILLERY_MAX_CHANCE = 0.70;
export const ARTILLERY_DAMAGE_BONUS = 0.50;
// ---------------------------------------------------------------------------
// id9: Building Destruction (Detonation) — `ships.md` "Passive Abilities".
// Computed for report visibility (matches the real sample reports' "Lower
// Building" section) but never applied — mutating building/planet state is
// a follow-up issue, deliberately out of scope for battle resolution.
// ---------------------------------------------------------------------------
/** Demolition points per id9 ship present, AT MAX UPGRADE LEVEL, by race. */
export const DEMOLITION_POINTS_PER_SHIP = {
    humans: 100, // Mammoth
    robots: 90, // Exterminator
    axons: 55, // Devoras
};
/** `ships.md` "Demolition Chance Table" — total points → chance to destroy N building level(s). */
export const DEMOLITION_CHANCE_TABLE = [
    { points: 0, chance: 0, buildingsAffected: 0 },
    { points: 20, chance: 0.20, buildingsAffected: 1 },
    { points: 101, chance: 0.40, buildingsAffected: 1 },
    { points: 201, chance: 0.60, buildingsAffected: 1 },
    { points: 401, chance: 0.50, buildingsAffected: 2 },
    { points: 551, chance: 0.70, buildingsAffected: 2 },
    { points: 701, chance: 0.50, buildingsAffected: 3 },
    { points: 851, chance: 0.60, buildingsAffected: 5 },
    { points: 1000, chance: 0.33, buildingsAffected: "all" },
];
// ---------------------------------------------------------------------------
// id-7 race-specific skills (asymmetric — unlike every other ship id)
// ---------------------------------------------------------------------------
/** Humans' Phoenix only. Rolled at the END of each round. */
export const REVIVAL_POWER_PER_SHIP = 0.0008;
export const REVIVAL_ACTIVATION_PER_SHIP = 0.0014;
export const REVIVAL_MAX_ACTIVATION_CHANCE = 0.70;
export const REVIVAL_MAX_REVIVE_PERCENT = 0.40;
/** Robots' Saboteur only ("Mega Power" / attack bonus). Rolled at the START of each round; excludes the Saboteur group itself. */
export const MEGA_POWER_ACTIVATION_PER_SHIP = 0.0014;
export const MEGA_POWER_ATTACK_BONUS_PER_SHIP = 0.0009;
export const MEGA_POWER_MAX_ACTIVATION_CHANCE = 0.70;
export const MEGA_POWER_MAX_ATTACK_BONUS = 0.80;
/**
 * Axons' Medusa only ("Freezing"). Rolled at the START of each round;
 * freezes one opponent group (no damage that round, still attackable).
 * User-specified numbers — override `ships.md`'s documented 0.04%/20%.
 */
export const FREEZING_CHANCE_PER_SHIP = 0.0005;
export const FREEZING_MAX_CHANCE = 0.30;
// ---------------------------------------------------------------------------
// Battle points (`docs/game-details/battle_system.md` "Battle Points")
// ---------------------------------------------------------------------------
export const BATTLE_POINTS_FACTOR = 2 / 3;
/** Applied instead of the normal formula when one side loses no units at all. */
export const BATTLE_POINTS_NO_LOSS_FACTOR = 4 / 3;
// ---------------------------------------------------------------------------
// Plunder ("Stolen Resources") — only when the attacker wins.
// ---------------------------------------------------------------------------
/** Fraction of the defender's metal/minerals/gas the attacker can loot on a win — Humans 66.6%, everyone else 50%. */
export const BATTLE_LOOT_PERCENT = {
    humans: 0.666,
    robots: 0.5,
    axons: 0.5,
};
// ---------------------------------------------------------------------------
// Recycle field / scrap ("Recycle Field")
// ---------------------------------------------------------------------------
/** `scrap = floor(destroyedCost × randomInt[MIN..MAX] × STEP_PERCENT)`. */
export const SCRAP_MULTIPLIER_STEP_PERCENT = 0.0075;
export const SCRAP_MULTIPLIER_MIN_STEP = 25;
export const SCRAP_MULTIPLIER_MAX_STEP = 35;
// ---------------------------------------------------------------------------
// Battle-roll budget (server/mock callers only — how many rolls to
// pre-generate before calling `resolveBattleSequential`, the shipped model)
// ---------------------------------------------------------------------------
/**
 * Rolls to pre-generate per battle round. `resolveBattleSequential`'s
 * population-scaled sub-chunking can fire up to `SEQ_CHUNK_MAX` (20)
 * sub-chunks per ship-type group per side, each consuming one crit roll —
 * unlike the dev-only simultaneous model's fixed per-group roll count, this
 * scales with how many distinct ship types are present.
 * Sized for the worst case (every ship type on both sides at max chunking,
 * each shot using one crit roll AND one Ignore Armor roll, since Ignore Armor
 * is rolled per hit) plus per-round skill/freezing/revival rolls, so a real
 * battle never runs the cursor dry — `RollCursor`'s exhausted-array fallback
 * (`next() → 1`, "never triggers") would otherwise silently drop crit and
 * Ignore Armor variance in the tail rounds of very large/long battles.
 * (Initiative is a single roll made once per battle, not per round, so it is
 * never at risk of running dry.)
 */
export const BATTLE_ROLLS_PER_ROUND = 1100;
/** Flat extra rolls appended once per battle (headroom for the scrap-yield roll). */
export const BATTLE_ROLLS_RESERVE = 50;
// ---------------------------------------------------------------------------
// Sequential model sub-chunking (`resolveBattleSequential`, the shipped model
// — see `chunkCountForPopulation` in `battleSequential.ts`)
// ---------------------------------------------------------------------------
/**
 * Chunk count scales smoothly with a group's round-start population, anchored
 * so 100->4, 1000->8, 10000->16, and capped at `SEQ_CHUNK_MAX` (~21k+ ships).
 * `SEQ_CHUNK_BASE * 2^(log10(pop / SEQ_CHUNK_ANCHOR_POP))` doubles per 10x of
 * population — "gradual increases, not jumps".
 */
export const SEQ_CHUNK_BASE = 4;
export const SEQ_CHUNK_ANCHOR_POP = 100;
export const SEQ_CHUNK_MAX = 20;
//# sourceMappingURL=battle.js.map